import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import api from '../api/client.js';

const CHANNELS = [
  { key: 'call', label: 'Called', icon: '📞' },
  { key: 'email', label: 'Emailed', icon: '✉️' },
  { key: 'text', label: 'Texted', icon: '💬' },
];

const OUTCOMES = [
  { key: 'left_voicemail', label: 'Left Voicemail' },
  { key: 'no_answer', label: 'No Answer' },
  { key: 'spoke_with_customer', label: 'Spoke With Customer' },
  { key: 'interested', label: 'Interested' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'wrong_number', label: 'Wrong Number' },
  { key: 'sent_information', label: 'Sent Information' },
  { key: 'need_different_contact', label: 'Need Different Contact' },
];

const CHANNEL_LABEL = Object.fromEntries(CHANNELS.map(c => [c.key, c.label]));
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map(o => [o.key, o.label]));

/**
 * "Log outreach" quick action (CR1) — logs a call/email/text in ~3 taps
 * without going through the full note-capture flow.
 *
 * Props:
 *   customerId, customerName — the account being logged against
 *   followUpId — optional; if set, offers to mark that follow-up done
 *   defaultContactName — optional pre-fill (e.g. from the follow-up's source note)
 *   onClose()
 *   onLogged() — called after a successful save, before onClose
 */
export default function LogOutreachModal({ customerId, customerName, followUpId, defaultContactName, onClose, onLogged }) {
  const qc = useQueryClient();
  const [channel, setChannel] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState('');
  const [contactName, setContactName] = useState(defaultContactName || '');
  const [markDone, setMarkDone] = useState(true);

  // Auto-fill the primary contact when one exists on the account (Ryan's
  // ask #4 — the CRM already knows who to talk to most of the time).
  const customer = useQuery({
    queryKey: ['customer', customerId, 'contacts-for-outreach'],
    queryFn: () => api.get(`/customers/${customerId}`).then(r => r.data.data),
    enabled: Boolean(customerId) && !defaultContactName,
  });
  const primaryContact = customer.data?.contacts?.find(c => c.is_primary) || customer.data?.contacts?.[0];
  const effectiveContactName = contactName || primaryContact?.name || '';

  const log = useMutation({
    mutationFn: async () => {
      const transcript = note.trim() || `${CHANNEL_LABEL[channel]} — ${OUTCOME_LABEL[outcome]}`;
      const { data } = await api.post('/notes', {
        customer_id: customerId,
        contact_name: effectiveContactName || null,
        touch_type: channel,
        outreach_outcome: outcome,
        transcript,
        follow_up_id: followUpId || null,
      });
      if (followUpId && markDone) {
        await api.put(`/followups/${followUpId}`, { status: 'done' });
      }
      return data.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['followups'] });
      qc.invalidateQueries({ queryKey: ['followup', String(followUpId)] });
      qc.invalidateQueries({ queryKey: ['customer', customerId] });
      onLogged?.();
      onClose();
    },
  });

  const needsContactName = channel === 'call' && !effectiveContactName.trim();
  const canSave = channel && outcome && !needsContactName && !log.isPending;

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-4 shadow-panel max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <div>
          <h3 className="text-lg font-semibold">Log outreach</h3>
          {customerName && <div className="text-sm text-muted">{customerName}</div>}
        </div>

        <div>
          <div className="label" id="lo-channel-label">How did you reach out?</div>
          <div className="flex gap-2" role="group" aria-labelledby="lo-channel-label">
            {CHANNELS.map(c => (
              <button key={c.key} type="button"
                className={clsx('btn-ghost text-sm flex-1', channel === c.key && 'bg-crimson text-white')}
                onClick={() => setChannel(c.key)}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>
        </div>

        {channel && (
          <div>
            <div className="label" id="lo-outcome-label">Outcome</div>
            <div className="flex flex-wrap gap-2" role="group" aria-labelledby="lo-outcome-label">
              {OUTCOMES.map(o => (
                <button key={o.key} type="button"
                  className={clsx('text-xs px-2 py-1 rounded border',
                    outcome === o.key ? 'bg-crimson text-white border-crimson' : 'border-navy-100 hover:bg-panel')}
                  onClick={() => setOutcome(o.key)}>
                  {o.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {channel === 'call' && (
          <div>
            <label htmlFor="lo-contact" className="label">Who did you talk to?</label>
            <input id="lo-contact" className="input" value={effectiveContactName}
                   onChange={e => setContactName(e.target.value)}
                   placeholder="Contact name" />
          </div>
        )}

        <div>
          <label htmlFor="lo-note" className="label">Note (optional)</label>
          <input id="lo-note" className="input" value={note} onChange={e => setNote(e.target.value)}
                 placeholder={channel && outcome ? `${CHANNEL_LABEL[channel]} — ${OUTCOME_LABEL[outcome]}` : 'One line is plenty'} />
        </div>

        {followUpId && (
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={markDone} onChange={e => setMarkDone(e.target.checked)} />
            Mark this follow-up done
          </label>
        )}

        {log.isError && (
          <div className="text-xs text-crimson">
            {log.error?.response?.data?.error || 'Failed to log outreach'}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn-primary text-sm" disabled={!canSave} onClick={() => log.mutate()}>
            {log.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
